/**
 * @typedef {(import("./Props").default)} Props
 * @typedef {(import("./State").default)} State
 */

import React from 'react';
import { BaseSVG } from '../../../shared/BaseSVG';
import { translate } from '../../Internationalization';
import images from '../../../config/local/images';

/** @extends {React.Component<Props, State>} */
export default class BaseModal extends React.Component {
	/** @param {Props} props */
	constructor(props) {
		super(props);

		/** @type {State} */
		this.state = {
			isOpen: true,
		};

		this.closeButtonRef = React.createRef();
		this._handleClose = this._handleClose.bind(this);
	}

	componentDidMount() {
		BaseModal.#disableWindowScroll(true);
		this.#focusCloseButton();
	}

	/** @returns {void} */
	// eslint-disable-next-line class-methods-use-this
	componentWillUnmount() {
		BaseModal.#disableWindowScroll(false);
	}

	#focusCloseButton() {
		setTimeout(() => this.closeButtonRef.current?.focus(), 0);
	}

	/**
	 * @param {boolean} hidden
	 * @returns {void}
	 */
	static #disableWindowScroll(hidden = true) {
		document.body.style.overflow = hidden ? 'hidden' : 'auto';
	}

	/** @returns {void} */
	_handleClose() {
		this.setState({ isOpen: false });
		BaseModal.#disableWindowScroll(false);

		if (this.props?.onClose) {
			this.props.onClose();
		}
	}

	/** @returns {JSX.Element} */
	#renderCloseButton() {
		return (
			<div className="flex justify-end">
				<button
					aria-label={translate('aria_labels.close_modal')}
					className="absolute top-2 right-2 p-2 rounded-md hover:bg-grey-500 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75"
					onClick={this._handleClose}
					ref={this.closeButtonRef}
					type="button"
				>
					<i aria-hidden="true" className="icon icon-xs icon-fill">
						<BaseSVG path={images.icons.close} />
					</i>
					<span className="sr-only">
						{translate('aria_labels.close_modal')}
					</span>
				</button>
			</div>
		);
	}

	/** @returns {JSX.Element | null} */
	render() {
		if (!this.state.isOpen) {
			return null;
		}

		return (
			<React.Fragment>
				{/* Overlay */}
				<div
					aria-hidden="true"
					className="fixed inset-0 z-10 bg-black bg-opacity-20"
					onClick={this._handleClose}
				/>

				{/* Modal */}
				<section
					aria-modal="true"
					className="fixed z-20 max-w-4xl w-fit p-6 rounded-lg overflow-y-auto bg-white shadow-lg"
					role="dialog"
					style={{
						left: '50%',
						maxHeight: '75vh',
						minWidth: '20%',
						top: '50%',
						transform: 'translate(-50%, -50%)',
					}}
				>
					{this.#renderCloseButton()}
					{this.props.children}
				</section>
			</React.Fragment>
		);
	}
}
